import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, BadgeCheck, Crown, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

const VerifiedDirectory = ({ variant = 'collapsed', onUpgradeClick }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [directoryCount, setDirectoryCount] = useState(0);
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadDirectoryCount();
  }, []);

  const loadDirectoryCount = async () => {
    try {
      const data = await apiService.getDirectoryCount();
      setDirectoryCount(data.active_count);
    } catch (error) {
      console.error('Error loading directory count:', error);
    }
  };

  const loadEntries = async () => {
    if (entries.length > 0) return; // Already loaded
    
    setIsLoading(true);
    try {
      const data = await apiService.getDirectoryEntries();
      setEntries(data.entries || []);
    } catch (error) {
      console.error('Error loading directory entries:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExpand = () => {
    if (!isExpanded) {
      loadEntries();
    }
    setIsExpanded(!isExpanded);
  };

  // Collapsed view for Free users - shows on homepage/results
  if (variant === 'collapsed') {
    return (
      <Card className="border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <CardTitle className="text-base font-semibold text-slate-800 dark:text-slate-100">
                Verified Professionals
              </CardTitle>
            </div>
            <Badge variant="secondary" className="bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
              {directoryCount} active
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <Button
            variant="ghost"
            className="w-full justify-between text-sm text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 p-2"
            onClick={handleExpand}
          >
            <span>View directory →</span>
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>

          {isExpanded && (
            <div className="mt-3 space-y-3">
              {isLoading ? (
                <div className="text-center py-4 text-sm text-slate-500 dark:text-slate-400">Loading...</div>
              ) : entries.length > 0 ? (
                <>
                  {/* Text-only entries */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {entries.slice(0, 5).map((entry) => (
                      <div 
                        key={entry.entry_id}
                        className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-100 dark:border-slate-600"
                      >
                        <p className="font-medium text-slate-800 dark:text-slate-100 text-sm">{entry.name}</p>
                        <p className="text-xs text-slate-600 dark:text-slate-400">{entry.credential_type}</p>
                        <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1 mt-1">
                          <BadgeCheck className="w-3 h-3" />
                          KaiCheckIt Verified — {entry.verification_status}
                        </p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                          Verification valid through {entry.verification_expiry}
                        </p>
                      </div>
                    ))}
                  </div>

                  {entries.length > 5 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => navigate('/directory')}
                    >
                      View all {directoryCount} professionals →
                    </Button>
                  )}
                </>
              ) : (
                <div className="text-center py-4 text-sm text-slate-500 dark:text-slate-400">
                  No verified professionals yet
                </div>
              )}

              {/* Premium upgrade microcopy */}
              <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                  Upgrade to distraction-free verification.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                  onClick={onUpgradeClick || (() => navigate('/pricing'))}
                >
                  <Crown className="w-3 h-3 mr-1" />
                  Premium removes listings and keeps the focus on evidence
                </Button>
              </div>

              {/* Disclaimer */}
              <p className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed">
                Disclaimer: The KaiCheckIt Directory lists professionals who have completed identity and credential verification.
                KaiCheckIt does not recommend, rank, or endorse any individual.
                Directory listings are informational only and do not constitute medical or professional advice.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Sidebar variant for results page
  if (variant === 'sidebar') {
    return (
      <Card className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <CardTitle className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Verified Professionals ({directoryCount} active)
            </CardTitle>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Professionals listed here have completed KaiCheckIt identity and credential verification.
            Listing is optional and does not imply endorsement.
          </p>
        </CardHeader>
        <CardContent className="pt-0">
          <Button
            variant="ghost"
            className="w-full justify-between text-sm text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 p-2"
            onClick={handleExpand}
          >
            <span>{isExpanded ? 'Collapse' : 'View directory →'}</span>
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>

          {isExpanded && (
            <div className="mt-3 space-y-2">
              {isLoading ? (
                <div className="text-center py-4 text-sm text-slate-500 dark:text-slate-400">Loading...</div>
              ) : entries.length > 0 ? (
                <>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {entries.slice(0, 8).map((entry) => (
                      <div 
                        key={entry.entry_id}
                        className="p-2 bg-slate-50 dark:bg-slate-700/50 rounded border border-slate-100 dark:border-slate-600"
                      >
                        <p className="font-medium text-slate-800 dark:text-slate-100 text-xs">{entry.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{entry.credential_type}</p>
                        <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                          <BadgeCheck className="w-2.5 h-2.5" />
                          Verified — {entry.verification_expiry}
                        </p>
                      </div>
                    ))}
                  </div>

                  {entries.length > 8 && (
                    <Button
                      variant="link"
                      size="sm"
                      className="w-full text-xs p-0 h-auto"
                      onClick={() => navigate('/directory')}
                    >
                      View all →
                    </Button>
                  )}
                </>
              ) : (
                <div className="text-center py-3 text-xs text-slate-500 dark:text-slate-400">
                  No professionals listed yet
                </div>
              )}

              {/* Upgrade CTA */}
              <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                  onClick={onUpgradeClick || (() => navigate('/pricing'))}
                >
                  <Crown className="w-3 h-3 mr-1" />
                  Go distraction-free with Premium
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default VerifiedDirectory;
